import { db, FirebaseDocRefType, FirebaseTimestamp, FirebaseWriteBatch } from 'firebase/index'
import parse from 'date-fns/parse'
import { push } from 'connected-react-router'
import { State } from 'index'
import { BusinessTimeType, OpenTimeType } from 'types/store'
import { StockType } from 'types/stock'
import { ScheduleType } from 'types/schedule'
import { TemporaryOpenDate } from 'types/calendar'
import { Action, Dispatch } from 'redux'
import { StoreCalendarType } from 'types/storeCalendar'
import { hideLoadingAction, showLoadingAction } from 'reducks/loading/actions'
import { dateToString, parseDateWithHyphen, parseTimeToNumber, parseToDate } from 'lib/helper'
import { getBusinessTimeOnDay, getOpenTimeOnDay } from 'lib/businessHour'
import { saveStoreInformationAction } from 'reducks/store/actions'
import { updateEditStateAction } from 'reducks/confirm/actions'
import { DaysOfWeek } from 'lib/daysOfWeek'
const daysOfWeek = new DaysOfWeek()

const saveStoreSettings = ({
  confirmOpenTime,
  confirmLastOrderTime,
  deletedStocks,
  updatedSchedules,
  temporaryOpenDates,
  isOpenTimeUpdated,
  storeRef,
  isAdmin,
}: {
  confirmOpenTime: OpenTimeType
  confirmLastOrderTime: BusinessTimeType
  deletedStocks: StockType[]
  updatedSchedules: ScheduleType[]
  temporaryOpenDates: TemporaryOpenDate[]
  isOpenTimeUpdated: boolean
  storeRef: FirebaseDocRefType
  isAdmin: boolean
}) => {
  return async (dispatch: Dispatch<Action>, getState: () => State) => {
    dispatch(showLoadingAction())

    const prevOpenTime = getState().store.business_hours
    const prevOrderStartTime = getState().store.order_start_time
    const prevLastOrderTime = getState().store.last_order_time
    const prevLastPickupTime = getState().store.last_pickup_time
    const specialHolidays = getState().calendar.specialHolidays
    const stocks: StockType[] = getState().stock.stocks
    const storeCalendars: StoreCalendarType[] = getState().storeCalendar.storeCalendars
    const updatedBreadRefs: FirebaseDocRefType[] = []
    const now = FirebaseTimestamp.now()
    const dailyStocks = stocks.filter((_stock) => _stock.type === 'daily')

    // 500件以上のbatch処理に対応できるように
    const batchArray: FirebaseWriteBatch[] = []
    batchArray.push(db.batch())
    let operationCounter = 0
    let batchIndex = 0

    // 当日以降の当日取り置き在庫のラストオーダー時刻を更新する
    for (let i = 0; i < dailyStocks.length; i = (i + 1) | 0) {
      const dailyStock = dailyStocks[i]
      const ref = dailyStock.stock_ref
      const dayOfWeek = daysOfWeek.getDayOfWeek(parseToDate(dailyStock.date), specialHolidays)
      const lastOrderTime = getBusinessTimeOnDay(dayOfWeek, confirmLastOrderTime)
      if (lastOrderTime) {
        dailyStock.deadline = FirebaseTimestamp.fromDate(
          parse(`${parseDateWithHyphen(dailyStock.date)} ${lastOrderTime}`, 'yyyy-MM-dd HH:mm', new Date())
        )
      } else {
        // 臨時営業日かどうか判定
        const storeCalendar = storeCalendars.find(
          (calendar) => calendar.date === dailyStock.date && calendar.status === 'open'
        )
        // 臨時営業日でなければ例外としてスキップ
        if (!storeCalendar) continue
        dailyStock.deadline = FirebaseTimestamp.fromDate(
          parse(
            `${parseDateWithHyphen(dailyStock.date)} ${storeCalendar.last_order_time}`,
            'yyyy-MM-dd HH:mm',
            new Date()
          )
        )
      }

      delete dailyStock.stock_ref
      if (!ref) continue
      batchArray[batchIndex].update(ref, dailyStock)
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
    }

    // 削除対象の在庫
    for (let i = 0; i < deletedStocks.length; i = (i + 1) | 0) {
      const deletedStock = deletedStocks[i]
      if (typeof deletedStock.stock_ref === 'undefined') continue
      batchArray[batchIndex].delete(deletedStock.stock_ref)
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
    }

    // 受渡解禁時刻が店舗オープン時刻より早い受渡可能日の受渡解禁時刻を更新する
    if (isOpenTimeUpdated) {
      const deletedStockRefs: FirebaseDocRefType[] = []

      const updatedStocks = stocks
        // 受渡解禁時刻が変更後の店舗オープン時刻より早いstockを抽出
        .filter((stock) => {
          const dayOfWeek = daysOfWeek.getDayOfWeek(parseToDate(stock.date), specialHolidays)
          const openTime = getOpenTimeOnDay(dayOfWeek, confirmOpenTime, stock.date, storeCalendars)
          if (!openTime) return false
          return parseTimeToNumber(stock.pickupable_time) <= parseTimeToNumber(openTime)
        })
        .reduce((prevStocks, stock) => {
          const dayOfWeek = daysOfWeek.getDayOfWeek(parseToDate(stock.date), specialHolidays)
          const openTime = getOpenTimeOnDay(dayOfWeek, confirmOpenTime, stock.date, storeCalendars)
          if (!openTime) return prevStocks
          if (!stock.stock_ref) return prevStocks
          const prevStockId = stock.stock_ref.id
          const nextStockId = prevStockId.slice(0, -5) + openTime // stock_idの末尾5桁を受渡解禁時刻に合わせる

          // 受渡解禁時刻がマージ対象か判定する
          const index = prevStocks.findIndex((prevStock: StockType) => {
            return (
              parseTimeToNumber(prevStock.pickupable_time) <= parseTimeToNumber(stock.pickupable_time) &&
              prevStock.bread_ref.id === stock.bread_ref.id &&
              prevStock.date === stock.date
            )
          })
          if (index === -1) {
            // idが異なれば古いstockを削除
            deletedStockRefs.push(stock.stock_ref)
            stock.pickupable_time = openTime
            stock.stock_ref = storeRef.collection('store_stocks').doc(nextStockId)
            return [...prevStocks, stock]
          } else {
            // 在庫数をマージする
            prevStocks[index].stock_ref = storeRef.collection('store_stocks').doc(nextStockId)
            prevStocks[index].stock = prevStocks[index].stock + stock.stock
            return prevStocks
          }
        }, [] as StockType[])

      for (let i = 0; i < deletedStockRefs.length; i = (i + 1) | 0) {
        const deletedStockRef = deletedStockRefs[i]
        batchArray[batchIndex].delete(deletedStockRef)
        operationCounter++
        if (operationCounter === 499) {
          batchArray.push(db.batch())
          batchIndex++
          operationCounter = 0
        }
      }

      for (let i = 0; i < updatedStocks.length; i = (i + 1) | 0) {
        const updatedStock = updatedStocks[i]
        const ref = updatedStock.stock_ref
        if (typeof ref === 'undefined') continue
        delete updatedStock.stock_ref
        batchArray[batchIndex].set(ref, updatedStock, { merge: true })
        operationCounter++
        if (operationCounter === 499) {
          batchArray.push(db.batch())
          batchIndex++
          operationCounter = 0
        }
      }
    }

    for (let i = 0; i < updatedSchedules.length; i = (i + 1) | 0) {
      const schedule = updatedSchedules[i]
      const ref = schedule.schedule_ref
      if (typeof ref === 'undefined') continue
      delete schedule.schedule_ref
      if (updatedBreadRefs.every((ref) => ref.id !== schedule.bread_ref.id)) {
        updatedBreadRefs.push(schedule.bread_ref)
      }
      batchArray[batchIndex].set(ref, schedule, { merge: true })
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
    }

    for (let i = 0; i < temporaryOpenDates.length; i = (i + 1) | 0) {
      const temporaryOpenDate = temporaryOpenDates[i]
      const date = dateToString(temporaryOpenDate.date)
      const dayOfWeek = daysOfWeek.getDayOfWeek(temporaryOpenDate.date, specialHolidays)
      const openTime = getOpenTimeOnDay(dayOfWeek, prevOpenTime, date, storeCalendars)
      const orderStartTime = getBusinessTimeOnDay(
        dayOfWeek,
        prevOrderStartTime,
        date,
        storeCalendars,
        'order_start_time'
      )
      const lastOrderTime = getBusinessTimeOnDay(dayOfWeek, prevLastOrderTime, date, storeCalendars, 'last_order_time')
      const lastPickupTime = getBusinessTimeOnDay(
        dayOfWeek,
        prevLastPickupTime,
        date,
        storeCalendars,
        'last_pickup_time'
      )
      if (!openTime || !orderStartTime || !lastOrderTime || !lastPickupTime) continue
      const id = `calendar_${storeRef.id}-${date}`
      const storeCalendarRef = storeRef.collection('store_calendars').doc(id)
      batchArray[batchIndex].set(
        storeCalendarRef,
        {
          date: date,
          open_time: openTime,
          order_start_time: orderStartTime,
          last_order_time: lastOrderTime,
          last_pickup_time: lastPickupTime,
          status: 'open',
        },
        { merge: true }
      )
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
    }

    for (let i = 0; i < updatedBreadRefs.length; i = (i + 1) | 0) {
      const breadRef = updatedBreadRefs[i]
      batchArray[batchIndex].set(breadRef, { updated_at: now }, { merge: true })
      operationCounter++
      if (operationCounter === 499) {
        batchArray.push(db.batch())
        batchIndex++
        operationCounter = 0
      }
    }

    // 店舗の設定を更新する
    const confirmStore = getState().confirm.store
    delete confirmStore.shop_ref
    batchArray[batchIndex].set(storeRef, confirmStore, { merge: true })

    await batchArray.forEach(async (batch) => await batch.commit())
    dispatch(hideLoadingAction())
    dispatch(saveStoreInformationAction(confirmStore))
    dispatch(updateEditStateAction(false))
    if (isAdmin) {
      dispatch(push(`/admin/shops/${storeRef.id}/information/complete`))
    } else {
      dispatch(push('/account/settings/complete'))
    }
  }
}

export default saveStoreSettings
